import React from "react";
import {Typography, theme as antdTheme} from "antd";

const {Text} = Typography;

/**
 * A static, non-interactive mock of the widget as the visitor will see it.
 *
 * Deliberately a hand-built mock rather than an embed of the real widget: the real one needs a
 * site key, an allow-listed origin and a live conversation, none of which exist while the admin is
 * still editing. This re-renders instantly from the unsaved form values, which is the point — the
 * admin sees the effect of a colour or a greeting before saving.
 */
const WidgetPreview = ({values, logo}) => {
    const {token} = antdTheme.useToken();
    const brand = values?.brandColor || "#6acc6f";
    const title = values?.title || values?.brandName || "پشتیبانی";
    const subtitle = values?.subtitle || "";
    const greeting = values?.greeting || "";
    const fields = (values?.preChatFields || []).filter((f) => f && f.label);
    const preChatOn = values?.preChatEnabled !== false;

    // Readable text on the brand fill — the same rule the widget itself applies.
    const onBrand = (() => {
        const h = String(brand).replace("#", "");
        if (h.length !== 6) return "#fff";
        const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
        return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#10131a" : "#ffffff";
    })();

    return (
        <div
            style={{
                width: 300,
                borderRadius: 16,
                overflow: "hidden",
                border: `1px solid ${token.colorBorderSecondary}`,
                boxShadow: token.boxShadow,
                background: token.colorBgContainer,
                direction: "rtl",
            }}
        >
            {/* header */}
            <div
                style={{
                    background: brand,
                    color: onBrand,
                    padding: "12px 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                }}
            >
                <div style={{display: "flex", alignItems: "center", gap: 8, minWidth: 0}}>
                    {logo && (
                        <img
                            src={logo}
                            alt=""
                            style={{
                                height: 26,
                                maxWidth: 74,
                                objectFit: "contain",
                                borderRadius: 4,
                                background: "rgba(255,255,255,.9)",
                                padding: 2,
                            }}
                        />
                    )}
                    <div style={{minWidth: 0}}>
                        <div style={{fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                            {title}
                        </div>
                        {subtitle && (
                            <div style={{fontSize: 10.5, opacity: 0.85, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                {subtitle}
                            </div>
                        )}
                    </div>
                </div>
                <span style={{opacity: 0.85, fontSize: 13}}>✕</span>
            </div>

            {/* body */}
            <div style={{padding: 14, background: token.colorFillQuaternary, minHeight: 210}}>
                {greeting && (
                    <div
                        style={{
                            background: token.colorBgContainer,
                            borderRadius: 10,
                            padding: "9px 11px",
                            fontSize: 11.5,
                            lineHeight: 1.9,
                            color: token.colorText,
                            marginBottom: 10,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {greeting}
                    </div>
                )}

                {preChatOn ? (
                    fields.length ? (
                        fields.slice(0, 4).map((f, i) => (
                            <div key={i} style={{marginBottom: 8}}>
                                <div style={{fontSize: 10.5, color: token.colorTextSecondary, marginBottom: 3}}>
                                    {f.label}
                                    {f.required && <span style={{color: token.colorError}}> *</span>}
                                </div>
                                <div
                                    style={{
                                        border: `1px solid ${token.colorBorder}`,
                                        borderRadius: 7,
                                        padding: "7px 9px",
                                        fontSize: 11,
                                        color: token.colorTextPlaceholder,
                                        background: token.colorBgContainer,
                                    }}
                                >
                                    {f.placeholder || (f.type === "SELECT" ? "—" : "")}
                                </div>
                            </div>
                        ))
                    ) : (
                        <Text type="secondary" style={{fontSize: 11}}>
                            هیچ فیلدی تعریف نشده
                        </Text>
                    )
                ) : (
                    <Text type="secondary" style={{fontSize: 11}}>
                        فرم اولیه غیرفعال است — گفت‌وگو مستقیم شروع می‌شود
                    </Text>
                )}

                {preChatOn && fields.length > 0 && (
                    <div
                        style={{
                            background: brand,
                            color: onBrand,
                            borderRadius: 8,
                            padding: "9px",
                            textAlign: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            marginTop: 10,
                        }}
                    >
                        شروع گفت‌وگو
                    </div>
                )}
                {fields.length > 4 && (
                    <Text type="secondary" style={{fontSize: 10, display: "block", marginTop: 6}}>
                        + {fields.length - 4} فیلد دیگر
                    </Text>
                )}
            </div>
        </div>
    );
};

export default WidgetPreview;
